interface SuccessScreenProps {
  event: {
    title: string;
    organizerName: string;
  };
  message: string;
  messagingConsentPrompt?: {
    isSubmitting: boolean;
    onAllow: () => void;
    onDecline: () => void;
  };
}

export default function SuccessScreen({ event, message, messagingConsentPrompt }: SuccessScreenProps) {
  return (
    <div className="font-display antialiased text-text-main h-screen flex flex-col bg-cream overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      <main className="flex-1 flex flex-col items-center justify-center p-6 w-full max-w-md mx-auto relative py-12" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <div className="text-center space-y-3 mb-10 animate-popIn w-full">
          <div className="relative mx-auto w-24 h-24 mb-6 flex items-center justify-center">
            <div className="absolute inset-0 bg-sky-soft rounded-full animate-pulse"></div>
            <span
              className="material-symbols-outlined text-sky-strong text-[4rem] relative z-10"
              style={{ fontVariationSettings: '"FILL" 1, "wght" 600' }}
            >
              check_circle
            </span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-text-main">
            You&apos;re all set.
          </h1>
          <p className="text-lg text-text-light font-bold leading-snug px-4">
            {event.organizerName} will pick the best time and let everyone know.
          </p>
        </div>

        <div className="w-full bg-white/60 backdrop-blur-sm border border-white rounded-2xl p-5 mb-8 text-center shadow-sm animate-[slideUp_0.6s_both_0.1s]">
          <p className="text-text-main font-bold text-base leading-snug">{message}</p>
          <p className="text-text-light text-sm font-semibold mt-1">
            Check this link for updates once it is confirmed.
          </p>
        </div>

        {messagingConsentPrompt ? (
          <div className="w-full bg-white rounded-3xl shadow-xl shadow-stone-200/50 p-6 border border-cream-dark/50 animate-[slideUp_0.6s_both_0.2s]">
            <div className="text-center mb-6">
              <h2 className="text-xl font-extrabold text-text-main leading-tight">
                Want CatchUp to text/email you updates for this hangout?
              </h2>
              <p className="text-sm text-text-light font-semibold mt-2 leading-snug">
                We&apos;ll only use this for updates about this plan.
              </p>
            </div>
            <div className="space-y-3">
              <button
                className="w-full py-4 rounded-2xl bg-primary text-white text-lg font-extrabold shadow-lg shadow-primary/25 hover:bg-primary-dark active:scale-[0.98] transition-all disabled:opacity-70"
                disabled={messagingConsentPrompt.isSubmitting}
                onClick={messagingConsentPrompt.onAllow}
                type="button"
              >
                Allow updates
              </button>
              <button
                className="w-full py-3 text-text-light font-bold text-sm hover:text-text-main transition-colors disabled:opacity-70"
                disabled={messagingConsentPrompt.isSubmitting}
                onClick={messagingConsentPrompt.onDecline}
                type="button"
              >
                Not now
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full bg-white rounded-3xl shadow-xl shadow-stone-200/50 p-6 border border-cream-dark/50 animate-[slideUp_0.6s_both_0.2s]">
            <div className="text-center mb-6">
              <h2 className="text-xl font-extrabold text-text-main leading-tight">
                Want this to be even easier next time?
              </h2>
            </div>
            <ul className="space-y-4 mb-8 px-2">
              <li className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-sky-soft flex items-center justify-center mt-0.5">
                  <span className="material-symbols-outlined text-sky-strong text-sm font-bold">
                    check
                  </span>
                </div>
                <span className="text-text-main font-bold text-sm">Respond with one tap</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-sky-soft flex items-center justify-center mt-0.5">
                  <span className="material-symbols-outlined text-sky-strong text-sm font-bold">
                    check
                  </span>
                </div>
                <span className="text-text-main font-bold text-sm">
                  Get push updates instead of texts
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="shrink-0 w-6 h-6 rounded-full bg-sky-soft flex items-center justify-center mt-0.5">
                  <span className="material-symbols-outlined text-sky-strong text-sm font-bold">
                    check
                  </span>
                </div>
                <span className="text-text-main font-bold text-sm">
                  Plan your own CatchUps in seconds
                </span>
              </li>
            </ul>
            <div className="space-y-3">
              <button className="w-full py-4 rounded-2xl bg-primary text-white text-lg font-extrabold shadow-lg shadow-primary/25 hover:bg-primary-dark active:scale-[0.98] transition-all flex items-center justify-center gap-2">
                Get the CatchUp app
              </button>
              <button className="w-full py-2 text-text-light font-bold text-sm hover:text-text-main transition-colors">
                Not now
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 text-center animate-[slideUp_0.6s_both_0.3s]">
          <p className="text-xs font-semibold text-text-light/70 tracking-wide">
            CatchUp is invite-only and spam-free.
          </p>
        </div>
      </main>
    </div>
  );
}
